import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { MetricCard } from '@/components/MetricCard';
import { LineChart, PieChart, ChartContainer } from '@/components/charts';
import { useDeviceHealth, useDeviceTrends, useFirmwareDistribution, useDevicesAttention } from '../hooks';
import {
  RefreshCw, Radio, CheckCircle, XCircle, Battery,
  Signal, Download, AlertTriangle
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { DeviceAttention } from '../types';

export default function DeviceHealthPage() {
  const { t } = useTranslation('intelligence');

  const { data: summary, isLoading: summaryLoading, refetch, isFetching } = useDeviceHealth();
  const { data: trends, isLoading: trendsLoading } = useDeviceTrends();
  const { data: firmware, isLoading: firmwareLoading } = useFirmwareDistribution();
  const { data: attention, isLoading: attentionLoading } = useDevicesAttention();

  const batteryTrendData = trends?.battery?.map((item: { date: string; avg_battery: number; low_battery_count: number }) => ({
    name: item.date,
    avgBattery: item.avg_battery,
    lowBatteryCount: item.low_battery_count,
  })) || [];

  const signalTrendData = trends?.signal?.map((item: { date: string; avg_rssi: number; weak_signal_count: number }) => ({
    name: item.date,
    avgRssi: item.avg_rssi,
    weakSignalCount: item.weak_signal_count,
  })) || [];

  // Limit to top 8 firmware versions and truncate long version names
  const firmwareChartData = firmware?.slice(0, 8).map(item => ({
    name: item.version?.length > 12 ? `${item.version.slice(0, 12)}...` : (item.version || 'Unknown'),
    value: item.count,
  })) || [];

  const getIssueType = (device: DeviceAttention) => {
    if (device.is_offline) return 'offline';
    if (device.battery_low) return 'lowBattery';
    if (device.signal_weak) return 'weakSignal';
    if (device.firmware_pending) return 'firmwarePending';
    return 'unknown';
  };

  const getIssueBadgeClass = (type: string) => {
    switch (type) {
      case 'offline': return 'bg-red-100 text-red-800';
      case 'lowBattery': return 'bg-orange-100 text-orange-800';
      case 'weakSignal': return 'bg-purple-100 text-purple-800';
      case 'firmwarePending': return 'bg-teal-100 text-teal-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="p-6 space-y-6 bg-gray-50 min-h-full">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('devices.title')}</h1>
          <p className="text-sm text-gray-500 mt-1">{t('devices.subtitle')}</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => refetch()}
          disabled={isFetching}
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${isFetching ? 'animate-spin' : ''}`} />
          {t('common:refresh')}
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
        <MetricCard
          title={t('devices.cards.totalDevices')}
          value={summary?.total_devices}
          icon={<Radio className="h-5 w-5" />}
          color="blue"
          loading={summaryLoading}
        />
        <MetricCard
          title={t('devices.cards.online')}
          value={(summary?.total_devices || 0) - (summary?.offline_count || 0)}
          icon={<CheckCircle className="h-5 w-5" />}
          color="green"
          loading={summaryLoading}
        />
        <MetricCard
          title={t('devices.cards.offline')}
          value={summary?.offline_count}
          icon={<XCircle className="h-5 w-5" />}
          color="red"
          loading={summaryLoading}
        />
        <MetricCard
          title={t('devices.cards.lowBattery')}
          value={summary?.low_battery_count}
          icon={<Battery className="h-5 w-5" />}
          color="orange"
          loading={summaryLoading}
        />
        <MetricCard
          title={t('devices.cards.weakSignal')}
          value={summary?.weak_signal_count}
          icon={<Signal className="h-5 w-5" />}
          color="purple"
          loading={summaryLoading}
        />
        <MetricCard
          title={t('devices.cards.firmwarePending')}
          value={summary?.firmware_pending_count}
          icon={<Download className="h-5 w-5" />}
          color="blue"
          loading={summaryLoading}
        />
      </div>

      {/* Trend Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>{t('devices.sections.batteryTrends')}</CardTitle>
          </CardHeader>
          <CardContent>
            <ChartContainer loading={trendsLoading} height={280}>
              <LineChart
                data={batteryTrendData}
                series={[
                  { dataKey: 'avgBattery', name: t('devices.charts.avgBattery'), color: '#27ae60' },
                  { dataKey: 'lowBatteryCount', name: t('devices.charts.lowBatteryCount'), color: '#e74c3c' },
                ]}
                height={250}
              />
            </ChartContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('devices.sections.signalTrends')}</CardTitle>
          </CardHeader>
          <CardContent>
            <ChartContainer loading={trendsLoading} height={280}>
              <LineChart
                data={signalTrendData}
                series={[
                  { dataKey: 'avgRssi', name: t('devices.charts.avgRssi'), color: '#3498db' },
                  { dataKey: 'weakSignalCount', name: t('devices.charts.weakSignalCount'), color: '#f39c12' },
                ]}
                height={250}
              />
            </ChartContainer>
          </CardContent>
        </Card>
      </div>

      {/* Firmware & Attention Table */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>{t('devices.sections.firmwareDistribution')}</CardTitle>
          </CardHeader>
          <CardContent>
            <ChartContainer loading={firmwareLoading} height={280}>
              <PieChart
                data={firmwareChartData}
                height={250}
                showLegend
                innerRadius={50}
              />
            </ChartContainer>
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-orange-500" />
              {t('devices.sections.attentionNeeded')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto max-h-[350px] overflow-y-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b sticky top-0">
                  <tr>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">
                      {t('devices.table.device')}
                    </th>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">
                      {t('devices.table.site')}
                    </th>
                    <th className="text-center text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">
                      {t('devices.table.issue')}
                    </th>
                    <th className="text-center text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">
                      {t('devices.table.battery')}
                    </th>
                    <th className="text-center text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">
                      {t('devices.table.signal')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {attentionLoading ? (
                    Array.from({ length: 5 }).map((_, i) => (
                      <tr key={i}>
                        {Array.from({ length: 5 }).map((_, j) => (
                          <td key={j} className="px-4 py-3">
                            <Skeleton className="h-4 w-full" />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : attention && attention.length > 0 ? (
                    attention.slice(0, 15).map((device, index) => {
                      const issueType = getIssueType(device);
                      return (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-4 py-3 font-medium text-gray-900">
                            {device.device_name || device.device_id}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {device.site_name}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <Badge className={cn('text-xs', getIssueBadgeClass(issueType))}>
                              {t(`devices.issues.${issueType}`)}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-center text-sm">
                            <span className={cn(
                              device.battery_level !== undefined && device.battery_level < 20 && 'text-red-600 font-semibold'
                            )}>
                              {device.battery_level !== undefined ? `${device.battery_level}%` : '--'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center text-sm">
                            <span className={cn(
                              device.signal_strength !== undefined && device.signal_strength < 3 && 'text-orange-600 font-semibold'
                            )}>
                              {device.signal_strength !== undefined ? device.signal_strength : '--'}
                            </span>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={5} className="px-4 py-12 text-center text-gray-500">
                        <CheckCircle className="h-12 w-12 mx-auto text-green-500 mb-2" />
                        <p>{t('devices.noIssues')}</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
